/**
 * G2eGente - Detalhe do Comunicado para Candidatos
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { newsFeedAPI, API_URL } from '../../lib/api';
import { ReactionBar } from '../../components/news/reactions';
import { NewsPostHeader } from '../../components/news/NewsPostHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  AlertTriangle,
  FileText,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const TIPO_CONFIG = {
  noticia: { label: 'Notícia', color: 'bg-blue-100 text-blue-700' },
  comunicado: { label: 'Comunicado', color: 'bg-green-100 text-green-700' },
  alerta: { label: 'Alerta', color: 'bg-red-100 text-red-700' },
  treinamento: { label: 'Treinamento', color: 'bg-purple-100 text-purple-700' }
};

const PRIORIDADE_CONFIG = {
  normal: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  alta: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

export default function CandidateNewsDetail() {
  const navigate = useNavigate();
  const { newsId } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsDetail();
  }, [newsId]);

  const fetchNewsDetail = async () => {
    try {
      const response = await newsFeedAPI.getCandidateDetail(newsId);
      setNews(response.data);
      
      // Marcar como lido
      await newsFeedAPI.markCandidateAsRead(newsId).catch(() => {});
    } catch (error) {
      toast.error('Erro ao carregar comunicado');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!news) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="candidate-news-detail">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-4 pt-8">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-cyan-100 text-sm">Comunicado</p>
            <h1 className="text-lg font-bold line-clamp-1">{news.titulo}</h1>
          </div>
        </div>
      </header>

      <div className="px-4 pt-4 pb-3 bg-white">
        <NewsPostHeader
          news={news}
          tipoLabel={TIPO_CONFIG[news.tipo]?.label || news.tipo}
          testId="candidate-news-detail-header"
        />
      </div>

      {news.imagem_capa && (
        <div className="w-full aspect-video bg-slate-200">
          <img 
            src={news.imagem_capa.startsWith('/') ? `${API_URL}${news.imagem_capa}` : news.imagem_capa} 
            alt={news.titulo} 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <main className="p-4">
        {news.prioridade !== 'normal' && (
          <Badge className={`mb-3 ${PRIORIDADE_CONFIG[news.prioridade]?.color}`}>
            {news.prioridade === 'urgente' && <AlertTriangle className="w-3 h-3 mr-1" />}
            {PRIORIDADE_CONFIG[news.prioridade]?.label}
          </Badge>
        )}

        {/* Title */}
        <h2 className="text-2xl font-bold text-slate-900 mb-2">{news.titulo}</h2>
        
        {/* Content */}
        <div 
          className="prose prose-slate max-w-none"
          dangerouslySetInnerHTML={{ __html: news.conteudo }}
        />

        <div className="mt-6 pt-4 border-t">
          <ReactionBar
            news={news}
            reactFn={newsFeedAPI.reactCandidate}
            onChange={(_, data) => setNews(prev => ({ ...prev, ...data }))}
          />
        </div>

        {/* Attachments */}
        {news.anexos && news.anexos.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Anexos ({news.anexos.length})
            </h3>
            <div className="space-y-2">
              {news.anexos.map((anexo, idx) => (
                <a
                  key={idx}
                  href={anexo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <div className="w-10 h-10 bg-white rounded flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate">{anexo.nome}</p>
                    <p className="text-xs text-slate-500">{anexo.tipo}</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Back Button */}
        <Button
          variant="outline"
          className="w-full mt-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar ao Feed
        </Button>
      </main>
    </div>
  );
}
